import {
  dateFormats
} from 'application/src/js/globals.js';
import {
  ajax,
  forceDatePickerRange,
  getDateString,
  scrollIntoView
} from 'application/src/js/functions.js';
import reducer from './reducer.js';

const datepickerOptions = {
  allowInput: true,
  altInput: true,
  altFormat: dateFormats[selectedLanguage],
  dateFormat: 'Y-m-d',
  locale: selectedLanguage
};

/**
 * Product Click Statistics Overview
 * @param {SpinnerOverlay} loader - SpinnerOverlay instance
 * @param {DataStorage} store - DataStorage instance
 */
export default class ProductOverview {
  constructor(args) {
    this.loader = args.loader;
    this.store = args.store;

    this.outerContainer = document.querySelector('.outer-container');
    this.productRows = this.outerContainer.querySelector('.product-rows');
    this.searchInput = document.querySelector('#search-products');
    this.startDateInput = this.outerContainer.querySelector('[name="start_date"]');
    this.endDateInput = this.outerContainer.querySelector('[name="end_date"]');
    this.analyzeButton = document.querySelector('.button-analyze');
    this.showMoreButton = this.outerContainer.querySelector('.show-more');
    this.showLessButton = this.outerContainer.querySelector('.show-less');

    this.filteredData = [];
    this.minProducts = 15;
    this.hideClass = 'hide';
    this.debugMode = true;

    this.initDatePicker();

    this.loadProducts()
      .then(response => {
        this.store.init(reducer, {
          products: response.data.clicks,
          selectedProductIds: []
        });

        this.addEvents();
        this.render(this.minProducts);
      })
      .catch(e => {
        if (this.debugMode) console.log(e);
        this.loader.hide();
      });
  }

  // initializes the start- end end-datepicker
  initDatePicker() {
    const currentDate = new Date();

    const startDate = new Date().setDate(currentDate.getDate() - 7);
    const endDate = new Date().setDate(currentDate.getDate() - 1);
    const endDateMax = new Date().setDate(currentDate.getDate() - 1);

    this.startDatePicker = flatpickr(
      this.startDateInput,
      Object.assign({},
        datepickerOptions, {
          defaultDate: startDate,
          maxDate: new Date().fp_incr(-2),
          onClose: (selectedDates, dateStr, instance) => {
            forceDatePickerRange(instance, this.endDatePicker, instance);
            this.update();
          }
        }
      )
    );

    this.endDatePicker = flatpickr(
      this.endDateInput,
      Object.assign({},
        datepickerOptions, {
          defaultDate: endDate,
          maxDate: endDateMax,
          onClose: (selectedDates, dateStr, instance) => {
            forceDatePickerRange(this.startDatePicker, instance, instance);
            this.update();
          }
        }
      )
    );
  }

  addEvents() {
    this.searchInput.addEventListener('input', e => {
      new Promise((resolve, reject) => {
        if (this.inputTimeout) clearTimeout(this.inputTimeout);
        this.inputTimeout = setTimeout(_ => resolve(), 200);
      }).then(_ => this.render());
    });

    this.showMoreButton.addEventListener('click', e => {
      this.render();
      this.showMoreButton.classList.add('hide');
      this.showLessButton.classList.remove('hide');
      this.scrollToTop();
    });

    this.showLessButton.addEventListener('click', e => {
      this.render(this.minProducts);
      this.showLessButton.classList.add('hide');
      this.showMoreButton.classList.remove('hide');
      this.scrollToTop();
    });

    this.analyzeButton.addEventListener('click', e => {
      if (this.store.getState().selectedProductIds.length === 0) {
        e.preventDefault();
        return;
      }
      this.loader.show();
    });
  }

  scrollToTop() {
    if (window.pageYOffset > this.outerContainer.offsetTop) scrollIntoView(this.outerContainer);
  }

  update() {
    this.loadProducts()
      .then(response => {
        this.store.dispatch({
          type: 'SET_PRODUCTS',
          payload: response.data.clicks
        });

        this.render();
      })
      .catch(e => {
        if (this.debugMode) console.log(e);
        this.loader.hide();
      });
  }

  loadProducts() {
    this.loader.show();

    let params = {};

    if (this.startDatePicker.selectedDates.length) {
      params.start_date = getDateString(this.startDatePicker.selectedDates[0]);
    }

    if (this.endDatePicker.selectedDates.length) {
      params.end_date = getDateString(this.endDatePicker.selectedDates[0]);
    }

    return ajax('/analytics/ajaxLoadClickStatistics', params);
  }

  toggleAnalyzeButton() {
    this.analyzeButton.classList[this.store.getState().selectedProductIds.length === 0 ? 'add' : 'remove']('disabled');
  }

  countClicks(products) {
    let clicks = 0;
    products.map(data => clicks += parseInt(data.amount));
    return clicks;
  }

  clicksMax(products) {
    let max = 0;

    products.map(data => {
      const amount = parseInt(data.amount);
      max = max < amount ? amount : max;
    });

    return max;
  }

  calcPercentages(filteredProducts) {
    const allProducts = this.store.getState().products;
    filteredProducts = [...filteredProducts];

    const allProductClicks = this.countClicks(allProducts);
    const allProductClicksMax = this.clicksMax(allProducts);
    const filteredProductClicks = this.countClicks(filteredProducts);
    const filteredProductClicksMax = this.clicksMax(filteredProducts);

    filteredProducts.map(data => {
      const amount = parseInt(data.amount);
      data.pct = (amount / (filteredProductClicks / 100));
      data.width = (amount / (filteredProductClicksMax / 100));
      data.pct2 = (amount / (allProductClicks / 100));
      data.width2 = data.pct2 * (100 / filteredProductClicksMax);
    });

    filteredProducts.sort((a, b) => b.amount - a.amount);

    return filteredProducts;
  }

  filterProducts(p) {
    let products = [...p];
    const searchValueRegex = this.searchInput.value.trim() === '' ? /.*/ : new RegExp('.*' + this.searchInput.value.trim() + '.*', 'gi');

    return products.reduce((filteredProducts, currentProduct) => {
      if (currentProduct.basicname.match(searchValueRegex) == null) return filteredProducts;
      filteredProducts.push(currentProduct);
      return filteredProducts;
    }, []);
  }

  render(showNumEntries) {
    this.productRows.innerHTML = '';
    const state = this.store.getState();
    const filteredData = this.calcPercentages(this.filterProducts(state.products));
    const numFiltered = filteredData.length;
    const numEntries = showNumEntries || numFiltered;

    filteredData.splice(numEntries);

    filteredData.forEach(productData => {
      const row = document.createElement('div');
      this.productRows.insertAdjacentElement('beforeend', row);

      new ProductStatisticRow({
        parent: this,
        element: row,
        data: productData,
        startDate: this.selectedStartDate,
        endDate: this.selectedEndDate,
        selected: state.selectedProductIds.indexOf(productData.global_product_basic_id) != -1
      })
    });

    this.setTargetRoute();

    if (numFiltered > numEntries) {
      this.showMoreButton.classList.remove(this.hideClass);
      this.showLessButton.classList.add(this.hideClass);
    } else if (numFiltered === numEntries) {
      this.showMoreButton.classList.add(this.hideClass);
      this.showLessButton.classList.add(this.hideClass);
    } else {
      this.showMoreButton.classList.add(this.hideClass);
      this.showLessButton.classList.remove(this.hideClass);
    }

    this.loader.hide();
  }

  get selectedStartDate() {
    return this.startDatePicker.selectedDates.length ? getDateString(this.startDatePicker.selectedDates[0]) : null;
  }

  get selectedEndDate() {
    return this.endDatePicker.selectedDates.length ? getDateString(this.endDatePicker.selectedDates[0]) : null;
  }

  setTargetRoute() {
    let paramString = this.store.getState().selectedProductIds.reduce((params, id) => {
      params.push(`products[]=${id}`);
      return params;
    }, []).join('&');

    if (this.startDatePicker.selectedDates.length) {
      paramString += `&start_date=${getDateString(this.startDatePicker.selectedDates[0])}`;
    }

    if (this.endDatePicker.selectedDates.length) {
      paramString += `&end_date=${getDateString(this.endDatePicker.selectedDates[0])}`;
    }

    this.analyzeButton.href = paramString ? `${location.origin}${location.pathname}/clickDetails/?${paramString}` : 'javascript://';
  }

  onProductSelect(productId) {
    this.store.dispatch({
      type: 'ADD_SELECTED_PRODUCT_ID',
      payload: productId
    });

    this.setTargetRoute();
    this.toggleAnalyzeButton();
  }

  onProductUnselect(productId) {
    this.store.dispatch({
      type: 'REMOVE_SELECTED_PRODUCT_ID',
      payload: productId
    });

    this.setTargetRoute();
    this.toggleAnalyzeButton();
  }
}

/**
 * Product Table Row
 * @class
 * @param {object} args.parent - The instance of the Analytics class
 * @param {object} args.element - The product row element
 * @param {object} args.data - The product data JSON
 */
class ProductStatisticRow {
  constructor(args) {
    this.parent = args.parent;
    this.element = args.element;
    this.data = args.data;
    this.startDate = args.startDate;
    this.endDate = args.endDate;
    this.selected = args.selected;

    this.render();
  }

  addEvents() {
    this.checkbox.addEventListener('change', e => {
      this.selected = !!e.target.checked;
      this.parent[this.selected ? 'onProductSelect' : 'onProductUnselect'](this.data.global_product_basic_id);
    });
  }

  render() {
    this.element.innerHTML = this.template;

    this.element.classList.add('list-row');
    this.checkbox = this.element.querySelector('input[type=checkbox]');
    this.showDetailsButton = this.element.querySelector('.show-details');

    this.addEvents();
  }

  get template() {
    return `
      <div class="product-select">
        <div class="uic-pretty-checkbox">
          <input
            type="checkbox"
            id="product-select-${this.data.global_product_basic_id}"
            value="${this.data.global_product_basic_id}"
            ${this.selected ? `checked` : ``}
          >
          <label for="product-select-${this.data.global_product_basic_id}"></label>
        </div>
      </div>
      <div class="product-title">${this.data.basicname}</div>
      <div class="product-chart">
        ${this.data.pct !== this.data.pct2 ? `
          <div class="bar small" style="width:${this.data.width2}%">
            <span>${this.data.pct2.toFixed(2)}%</span>
          </div>
        ` : ``}
        <div class="bar ${this.data.pct !== this.data.pct2 ? `small` : ``}" style="width:${this.data.width}%">
          <span>${this.data.pct.toFixed(2)}%</span>
        </div>
      </div>
      <div class="details-button"><a class="show-details" href="/analytics/clickDetails/?products[]=${this.data.global_product_basic_id}${this.startDate ? `&start_date=${this.startDate}` : ``}${this.endDate ? `&end_date=${this.endDate}` : ``}">Details</a></div>
    `;
  }
}